import api from '../../../api/axios';
import { API_ENDPOINTS } from '../../../api/endpoints';

type ApiRecord = Record<string, unknown>;

const asRecord = (value: unknown): ApiRecord | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as ApiRecord)
    : null;

const asArray = <T>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : []);

export interface VesselPlanningMasterRow {
  Id?: number | null;
  VESSELPLANINGNo?: number | null;
  VESSELPLANINGNoDisplay?: string;
  VESSELPLANINGDate?: string;
  Remarks?: string;
}

export interface VesselPlanningDetailRow {
  Id?: number | null;
  SDId?: number | null;
  VESSELPLANINGMasterRefId?: number | null;
  SaleOrderMasterRefId?: number | null;
  SortBy?: number | null;
  JobNo?: string;
  JobDate?: string;
  JobStatus?: string;
  Origin?: string;
  Destination?: string;
  SETA?: string;
  SETB?: string;
  SETD?: string;
  SOETA?: string;
  SOETB?: string;
  SOETD?: string;
  SPickupDate?: string;
  SDeliveryDate?: string;
  SWareHouseEnterDate?: string;
  SWareHouseExitDate?: string;
  WareHouseAddress?: string;
  pkg?: string;
  Loadingvesselname?: string;
  Offvesselname?: string;
  BLCopy?: string;
  TruckSize?: string;
  SCN?: string;
  LSCN?: string;
  Commodity?: string;
  Vessel?: string;
  OVessel?: string;
  SPort?: string;
  OPort?: string;
  JobName?: string;
  AWBNo?: string;
  Remarks1?: string;
  Cargo?: string;
  PTW?: string;
  ZB?: string;
  ZB2?: string;
  ZBRef?: string;
  ZBRef2?: string;
  PortCharges?: number | null;
  PortChargesRef?: string;
  AgentName?: string;
  AgentPhone?: string;
  OAgentName?: string;
  OAgentPhone?: string;
  LBoardingOfficerRefid?: number | null;
  LBoardingOfficerName?: string;
  LBoardingOfficer1Refid?: number | null;
  LBoardingOfficerName1?: string;
  LBoardingOfficer2Refid?: number | null;
  LBoardingOfficerName2?: string;
  LBoardingAmount?: number | null;
  LBoardingAmount1?: number | null;
  LBoardingAmount2?: number | null;
  OBoardingOfficerRefid?: number | null;
  OBoardingOfficerName?: string;
  OBoardingOfficer1Refid?: number | null;
  OBoardingOfficerName1?: string;
  OBoardingOfficer2Refid?: number | null;
  OBoardingOfficerName2?: string;
  OBoardingAmount?: number | null;
  OBoardingAmount1?: number | null;
  OBoardingAmount2?: number | null;
  CustomerName?: string;
  EmployeeName?: string;
  /** Person in charge - SaleOrderMaster.EmployeeRefId. */
  PICName?: string;
  Remarks?: string;
}

export interface VesselPlanningSelectResponse {
  salemaster: VesselPlanningMasterRow[];
  saledetails: VesselPlanningDetailRow[];
}

export interface VesselPlanningEditResponse {
  Id?: number | null;
  CompanyRefId?: number | null;
  UserRefId?: number | null;
  EmployeeRefId?: number | null;
  SFDate?: string;
  STDate?: string;
  SaleDate?: string;
  SSaleDate?: string;
  CNumberDisplay?: string;
  CNumber?: number | null;
  Remarks?: string;
  Search?: string;
  Active?: number | null;
  SaleDetails?: VesselPlanningDetailRow[];
}

export interface VesselPlanningSearchRequest {
  comid: number;
  search?: string;
  employeeid?: number;
  fromdate: string;
  todate: string;
  etaType?: number;
  DeliveryDone?:boolean;
}

export interface VesselPlanningSaveDetailRequest {
  SaleOrderMasterRefId: number;
  Remarks: string;
}

export interface VesselPlanningSaveRequest {
  Id: number;
  CompanyRefId: number;
  UserRefId: number | null;
  EmployeeRefId: number | null;
  FDate: string;
  TDate: string;
  SaleDate: string;
  CNumberDisplay: string;
  CNumber: number;
  Remarks: string;
  Search: string;
  SaleDetails: VesselPlanningSaveDetailRequest[];
}

export interface VesselPlanningSaveResult {
  ok?: boolean;
  message?: string;
  name?: string;
  id?: number | null;
}

export interface VesselPlanningNumberResponse {
  sequenceNumber?: string;
  companyId?: number;
  success?: boolean;
  error?: string;
}

/**
 * Body of POST /api/vessel-plannings/sale-order-update - the Update window. Only what that
 * window edits; the server writes nothing else on the sale order.
 */
export interface VesselPlanningSaleOrderUpdateRequest {
  saleOrderId: number;
  companyId: number;
  /** JobStatusMaster id; null keeps the job's status. */
  jobStatusId: number | null;
  /** Null keeps the job's cargo. */
  cargo: string | null;
  ptw: string;
  /** 'yyyy-MM-dd HH:mm:ss', or '' when the date's box is unticked (clears it). */
  eta: string;
  etb: string;
  etd: string;
  oeta: string;
  oetb: string;
  oetd: string;
  loadingOfficer1: number | null;
  loadingOfficer2: number | null;
  loadingOfficer3: number | null;
  offOfficer1: number | null;
  offOfficer2: number | null;
  offOfficer3: number | null;
}

/** What the job holds after the update, in the grid's formats. */
export interface VesselPlanningSaleOrderUpdateResponse {
  ok: boolean;
  message?: string;
  saleOrderId: number;
  jobStatusId: number | null;
  jobStatus: string;
  cargo: string;
  ptw: string;
  seta: string;
  setb: string;
  setd: string;
  soeta: string;
  soetb: string;
  soetd: string;
  loadingOfficer1: number | null;
  loadingOfficer2: number | null;
  loadingOfficer3: number | null;
  loadingAmount1: number | null;
  loadingAmount2: number | null;
  loadingAmount3: number | null;
  offOfficer1: number | null;
  offOfficer2: number | null;
  offOfficer3: number | null;
  offAmount1: number | null;
  offAmount2: number | null;
  offAmount3: number | null;
}

/**
 * The Update window on several ticked jobs. Null means "keep" on every field; an officer side
 * is replaced only when its list is sent.
 */
export interface VesselPlanningSaleOrderBulkUpdateRequest {
  companyId: number;
  saleOrderIds: number[];
  jobStatusId: number | null;
  cargo: string | null;
  ptw: string | null;
  /** null keep, '' clear, else 'yyyy-MM-dd HH:mm:ss' set on every job. */
  eta: string | null;
  etb: string | null;
  etd: string | null;
  oeta: string | null;
  oetb: string | null;
  oetd: string | null;
  /** Three slots (null = empty slot); null list = keep each job's side. */
  loadingOfficers: (number | null)[] | null;
  offOfficers: (number | null)[] | null;
}

/**
 * The same bulk update with only one leg's dates (Vessel changes page): every field left out is
 * kept, exactly as null is.
 */
export type VesselPlanningSaleOrderBulkDatesRequest =
  Pick<VesselPlanningSaleOrderBulkUpdateRequest, 'companyId' | 'saleOrderIds'>
  & Partial<Pick<VesselPlanningSaleOrderBulkUpdateRequest, 'eta' | 'etd' | 'oeta' | 'oetd'>>;

export interface VesselPlanningSaleOrderBulkUpdateResult {
  saleOrderId: number;
  jobNo: string;
  ok: boolean;
  message: string;
  /** Present when ok. */
  saved: VesselPlanningSaleOrderUpdateResponse | null;
}

export interface VesselPlanningSaleOrderBulkUpdateResponse {
  requested: number;
  updated: number;
  skipped: number;
  results: VesselPlanningSaleOrderBulkUpdateResult[];
}

const unwrapSelectResponse = (value: unknown): VesselPlanningSelectResponse => {
  const record = asRecord(value);
  if (record?.salemaster || record?.saledetails) {
    return {
      salemaster: asArray<VesselPlanningMasterRow>(record.salemaster),
      saledetails: asArray<VesselPlanningDetailRow>(record.saledetails),
    };
  }

  const first = Array.isArray(value) ? asRecord(value[0]) : null;
  return {
    salemaster: asArray<VesselPlanningMasterRow>(first?.salemaster),
    saledetails: asArray<VesselPlanningDetailRow>(first?.saledetails),
  };
};

const unwrapEditResponse = (value: unknown): VesselPlanningEditResponse => {
  if (Array.isArray(value)) {
    return (value[0] as VesselPlanningEditResponse) ?? {};
  }
  return (asRecord(value) as VesselPlanningEditResponse) ?? {};
};

const unwrapSaveResponse = (value: unknown): VesselPlanningSaveResult => {
  if (Array.isArray(value)) {
    return (value[0] as VesselPlanningSaveResult) ?? {};
  }
  return (asRecord(value) as VesselPlanningSaveResult) ?? {};
};

export const vesselPlanningApi = {
  getList: async (params: {
    comid: number;
    employeeid?: number;
    search?: string;
    fromdate?: string;
    todate?: string;
  }): Promise<VesselPlanningSelectResponse> => {
    const { data } = await api.post(API_ENDPOINTS.VESSEL_PLANNING.SELECT, params);
    return unwrapSelectResponse(data);
  },

  getById: async (
    id: number | string,
    companyId: number,
    vesselPlanningNo?: number | string
  ): Promise<VesselPlanningEditResponse> => {
    const { data } = await api.get(API_ENDPOINTS.VESSEL_PLANNING.EDIT, {
      params: {
        id,
        companyId,
        vesselPlanningNo,
      },
    });
    return unwrapEditResponse(data);
  },

  getMaxNo: async (companyId: number): Promise<VesselPlanningNumberResponse> => {
    const { data } = await api.post(API_ENDPOINTS.VESSEL_PLANNING.MAX_NO(companyId));
    return (asRecord(data) as VesselPlanningNumberResponse) ?? {};
  },

  searchSaleOrders: async (
    payload: VesselPlanningSearchRequest
  ): Promise<VesselPlanningDetailRow[]> => {
    const { data } = await api.post(API_ENDPOINTS.VESSEL_PLANNING.SEARCH, payload);
    return asArray<VesselPlanningDetailRow>(data);
  },

  save: async (
    payload: VesselPlanningSaveRequest[],
    companyId: number
  ): Promise<VesselPlanningSaveResult> => {
    const { data } = await api.post(API_ENDPOINTS.VESSEL_PLANNING.SAVE, payload, {
      headers: { Comid: companyId },
    });
    return unwrapSaveResponse(data);
  },

  delete: async (
    id: number | string,
    companyId: number
  ): Promise<VesselPlanningSaveResult> => {
    const { data } = await api.delete(API_ENDPOINTS.VESSEL_PLANNING.DELETE(id), {
      params: { companyId },
    });
    return unwrapSaveResponse(data);
  },

  /**
   * Update window save. A 400 (bad date, closed status with open purchase orders) is a real
   * answer, so there is no fallback to the full sale order PUT.
   */
  updateSaleOrder: async (
    payload: VesselPlanningSaleOrderUpdateRequest
  ): Promise<VesselPlanningSaleOrderUpdateResponse> => {
    const { data } = await api.post(API_ENDPOINTS.VESSEL_PLANNING.SALE_ORDER_UPDATE, payload);
    return data as VesselPlanningSaleOrderUpdateResponse;
  },

  /** Update window on many jobs. Always 200 with one result per job; 400 only when nothing was filled in. */
  updateSaleOrders: async (
    payload: VesselPlanningSaleOrderBulkUpdateRequest | VesselPlanningSaleOrderBulkDatesRequest
  ): Promise<VesselPlanningSaleOrderBulkUpdateResponse> => {
    const { data } = await api.post(API_ENDPOINTS.VESSEL_PLANNING.SALE_ORDER_UPDATE_MANY, payload);
    return data as VesselPlanningSaleOrderBulkUpdateResponse;
  },

  getView: async (soId: number, companyId: number) => {
    const { data } = await api.post(API_ENDPOINTS.VESSEL_PLANNING.VIEW, {
      SoId: soId,
      Comid: companyId,
    });
    return asArray(data);
  },
};

export default vesselPlanningApi;
